import { useState } from "react";

/**
 * Server-pagination state that snaps `page` back to 0 whenever the result set
 * changes (a different module is selected, a filter/search is applied, the
 * parent resource changes, etc.).
 *
 * With server-side pagination, carrying a stale page (e.g. page 2) into a
 * selector that has fewer pages refetches a page that no longer exists, leaving
 * the grid empty with broken pagination. This hook prevents that.
 *
 * It uses React's "adjust state during render" pattern, so the reset is applied
 * BEFORE the consumer's data-fetch effect runs — no stale fetch and no empty
 * flash, unlike a reset performed in a separate effect.
 *
 * The previous key is held in STATE, not a ref: mutating a ref during render is
 * unsafe under React StrictMode's double-invoke (the ref would update on the
 * first pass, so the second pass would skip the reset and the page would never
 * reset). State updates during render are replayed consistently, so this is the
 * StrictMode-safe form of the pattern.
 *
 * @param {Object} params
 * @param {number} [params.pageSize=25] Initial page size.
 * @param {string|number} params.resetKey A *primitive* that is a stable
 *   serialization of everything that changes which rows the server returns
 *   (module id, filter AST/search, parent id, ...) but NOT `page` itself.
 *   Serialize object selectors (e.g. `JSON.stringify(filterAst)`) before passing.
 * @returns {[{page:number,pageSize:number}, Function]} The `paginationModel`
 *   tuple, drop-in compatible with the existing `[model, setModel]` usage.
 */
export function useResettablePagination({ pageSize = 25, resetKey }) {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize });
  const [prevKey, setPrevKey] = useState(resetKey);

  if (!Object.is(prevKey, resetKey)) {
    setPrevKey(resetKey);
    if (paginationModel.page !== 0) {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
  }

  return [paginationModel, setPaginationModel];
}
